/* eslint-disable no-unused-vars */
import React, { useEffect, useRef, useState } from "react";
import { CircularProgress, Box, Typography, Pagination, Alert, Button, TextField, MenuItem } from "@mui/material"; // Material UI components for loading spinner and text
import MachineCard from "../../components/MachineCard/MachineCard";
import ContextFilterAndSort from "../../components/ContextFilterAndSort/ContextFilterAndSort";
import "./MyMachines.css";
import PageTitle from "../../components/PageTitle/PageTitle";
import { useSite } from "../../context/siteContext";
import { useAuth } from "../../context/authContext";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import ViewInArOutlinedIcon from "@mui/icons-material/ViewInArOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { Download, Inventory as InventoryIcon } from "@mui/icons-material";
import { ALERT_MESSAGES, LABELS, PLACEHOLDER, UI_TEXT } from "../../constants";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../hooks/useDebounce";
import { getMachinesByIds, getMachinesWithModels } from "../../services/myMachineService";
import { ASSETS_BASE_URL } from "../../config";
import ModelIcon from "../../components/ModelIcon/ModelIcon";
import ModelPDFIcon from "../../components/ModelPDFIcon/ModelPDFIcon";
import { PiTreeStructureFill } from "react-icons/pi";
import { trackEvent } from "../../utilities/analyticsService";
import { useLocation } from "react-router-dom";

const MyMachines = () => {
	const [machines, setMachines] = useState([]);
	const [loading, setLoading] = useState(true);
	const [isSearching, setIsSearching] = useState(false); // NEW: Track search loading separately
	const [error, setError] = useState(null);
	const [pageSize, setPageSize] = useState(50); // Default page size
	const [currentPage, setCurrentPage] = useState(1); // Default current page
	const [totalPages, setTotalPages] = useState(0); // Total number of pages based on pageSize and totalItems
	const [machineSearchTerm, setMachineSearchTerm] = useState("");
	const { selectedSite, siteData, selectedSiteData, currencySymbol } = useSite();
	const { canAddToCart } = useAuth();
	const [sortOption, setSortOption] = useState("");
	const [iconsLoadingById, setIconsLoadingById] = useState({});
	const { t } = useTranslation();
	const debouncedSearchTerm = useDebounce(machineSearchTerm, 500);
	const lastTrackedSearchRef = useRef("");

	const sortOptions = [t('GENERAL.NAME_ASC'), t('GENERAL.NAME_DESC'), t('GENERAL.NUMBER_ASC'), t('GENERAL.NUMBER_DESC')];
	const pageSizeOptions = [25, 50, 100];
	const location = useLocation();
	useEffect(() => {
		if (!selectedSite) {
			setMachines([]);
			return;
		}

		// Determine if this is initial load or search/filter operation
		const isInitialLoad = loading && !machines.length;
		const isSearchOrFilter = !isInitialLoad;

		if (isSearchOrFilter) {
			setIsSearching(true);
		} else {
			setLoading(true);
		}

		const fetchMachines = async () => {
			try {
				// The request now sends all parameters to the backend
				const reqBody = {
					site_number: selectedSite,
					page: currentPage,
					limit: pageSize,
					searchTerm: debouncedSearchTerm,
					sortBy: sortOption,
				};

				const response = await getMachinesWithModels({
					...reqBody,
					includeModelData: false,
					includeBomCount: false,
				});

				if (response.status) {
					// State is set directly from the paginated API response
					const machinesWithFlags = (response.data || []).map((machine) => ({
						...machine,
						model_data_loaded: Boolean(machine.model_data),
					}));
					setMachines(machinesWithFlags);
					setTotalPages(response.pagination.totalPages);

					const trimmedSearch = debouncedSearchTerm.trim();
					const resultCount = Number(response.pagination?.totalItems ?? machinesWithFlags.length) || 0;
					const trackingKey = `${trimmedSearch.toLowerCase()}|${selectedSite}|my_machines|${resultCount}`;

					if (trimmedSearch && lastTrackedSearchRef.current !== trackingKey) {
						lastTrackedSearchRef.current = trackingKey;

						trackEvent("search_performed", {
							search_term: trimmedSearch,
							search_source: "my_machines",
							page_name: "My Machines",
							page_path: `${location.pathname}${location.search || ""}`,
							result_count: resultCount,
							has_results: resultCount > 0,
							no_results: resultCount === 0,
						});
					}
					setError(null); // Clear any previous errors
				} else {
					setError(t('ERRORS.FAILED_TO_FETCH_MACHINE_DATA'));
				}
			} catch (err) {
				setError(t('ERRORS.AN_ERROR_OCCURRED_WHILE_FETCHING_DATA') + ": " + err.message);
			} finally {
				setLoading(false);
				setIsSearching(false);
			}
		};

		fetchMachines();
	}, [
		currentPage,
		pageSize,
		selectedSite,
		debouncedSearchTerm,
		sortOption,
		location.pathname,
		location.search,
	]);
	useEffect(() => {
		if (!machines.length) {
			setIconsLoadingById({});
			return;
		}

		const idsToLoad = machines
			.filter((machine) => machine?.machine_id && !machine.model_data_loaded)
			.map((machine) => machine.machine_id);

		if (!idsToLoad.length) return;

		setIconsLoadingById((prev) => {
			const next = { ...prev };
			idsToLoad.forEach((id) => {
				next[id] = true;
			});
			return next;
		});

		let canceled = false;
		const loadIcons = async () => {
			try {
				const response = await getMachinesByIds({ machines: idsToLoad });
				if (!response?.status || canceled) return;
				const fetched = response.data || [];
				const fetchedById = new Map(
					fetched.map((machine) => [machine.machine_id, machine])
				);

				setMachines((prev) =>
					prev.map((machine) => {
						const updated = fetchedById.get(machine.machine_id);
						if (!updated) return machine;
						return {
							...machine,
							...updated,
							model_data_loaded: true,
						};
					})
				);
			} catch (err) {
				// Keep cards visible even if icon data fails
				console.warn("Failed to load machine icons:", err);
			} finally {
				if (canceled) return;
				setIconsLoadingById((prev) => {
					const next = { ...prev };
					idsToLoad.forEach((id) => {
						delete next[id];
					});
					return next;
				});
			}
		};

		const idleHandle = setTimeout(loadIcons, 0);

		return () => {
			canceled = true;
			clearTimeout(idleHandle);
		};
	}, [machines]);

	const handleSortChange = (value) => {
		setCurrentPage(1); // Reset to page 1 when sorting
		setSortOption(value);
	};

	const handleMachineSearchChange = (value) => {
		setCurrentPage(1); // Reset to page 1 when searching
		setMachineSearchTerm(value);
	};

	const handlePageSizeChange = (event) => {
		setCurrentPage(1);
		setPageSize(Number(event.target.value));
	};

	// Show full-page loading only on initial load
	if (loading && machines.length === 0) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
				<CircularProgress />
			</Box>
		);
	}

	// Show full-page error only if no data was loaded previously
	if (error && machines.length === 0) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
				<Alert severity="error" className="error-alert">
					{error}
				</Alert>
			</Box>
		);
	}

	return (
		<div className="machines-page">
			<PageTitle title={t('LABELS.MACHINESS')} />

			<Box sx={{ width: "100%", display: 'flex', justifyContent: 'space-between', alignItems: 'center', columnGap: 2, mb: 2 }}>
				{/* Add data-tour to the legend/info box */}
				<Box data-tour="product-3d-model">
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							flexWrap: "wrap",
							gap: 1,
							color: "#aaa",
							fontSize: "11px"
						}}
					>
						<Typography variant="body1" fontSize="11px">
							<b>{t("UI_TEXT.INDEX")}:</b>
						</Typography>
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
							<ModelIcon fontSize="small" sx={{ mx: "3px" }} />: {t("UI_TEXT.MODEL_3D")}
						</Box>
						<Box component="span">|</Box>
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
							<ModelPDFIcon fontSize="small" sx={{ mx: "3px" }} />: {t("UI_TEXT.TWOD_MODEL")}
						</Box>
						<Box component="span">|</Box>
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
							<DescriptionOutlinedIcon fontSize="small" sx={{ mx: "3px" }} />: {t("UI_TEXT.DOCUMENTATIONS")}
						</Box>

						<Box component="span">|</Box>
						<Box component="span" sx={{ display: "inline-flex", alignItems: "center", whiteSpace: "nowrap" }}>
							<PiTreeStructureFill size={14} style={{ margin: "0 3px" }} />: {t("UI_TEXT.VIEW_BOM")}
						</Box>
					</Box>
				</Box>

				<Box sx={{ width: "100%", flex: 1, display: 'flex', justifyContent: 'flex-end', alignItems: 'center', columnGap: 2 }}>
					{canAddToCart() && (
						<Button
							variant="text"
							color="primary"
							size="small"
							startIcon={<Download />}
							onClick={() => {
								window.open(`${ASSETS_BASE_URL}/Templates/partsOrderTemplate.csv`, "_blank");
							}}
						>
							{t('BUTTON.PARTS_ORDER_TEMPLATE')}
						</Button>
					)}
					<Box sx={{ width: "140px" }}>
						<TextField
							select
							size="small"
							label="Per page"
							value={pageSize}
							onChange={handlePageSizeChange}
							fullWidth
							SelectProps={{
								MenuProps: {
									disableScrollLock: true,
								},
							}}
						>
							{pageSizeOptions.map((size) => (
								<MenuItem key={size} value={size}>
									{size}
								</MenuItem>
							))}
						</TextField>
					</Box>
					<Box sx={{ maxWidth: '300px', width: '300px' }} data-tour="search-machine">
						<ContextFilterAndSort
							value={machineSearchTerm}
							onSearchChange={handleMachineSearchChange}
							placeholder={t('PLACEHOLDER.SEARCH_MACHINES')}
							options={sortOptions}
							selectedOption={sortOption}
							onSortChange={handleSortChange}
							showSearch={true}
							showSort={true}
							disabled={isSearching}
						/>
					</Box>
				</Box>
			</Box>

			{/* Add data-tour to the machines list container */}
			<div className="machines-list-container" data-tour="machines-list">
				{error && machines.length > 0 && (
					<Alert severity="error" sx={{ marginBottom: 2 }}>
						{error}
					</Alert>
				)}

				{isSearching && (
					<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
						<CircularProgress size={30} />
						<Typography sx={{ ml: 2 }}>{t('UI_TEXT.SEARCHING')}</Typography>
					</Box>
				)}

				{!isSearching && (
					<>
						{machines.length === 0 ? (
							<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "50vh" }}>
								<Alert severity="info" sx={{ width: "fit-content" }}>
									{debouncedSearchTerm ?
										`No machines found matching "${debouncedSearchTerm}"` :
										t('ALERT_MESSAGES.NO_MACHINES_FOUND_FOR_THE_SELECTED_SITE')
									}
								</Alert>
							</Box>
						) : (
							machines.map((machine, index) => (
								<div key={machine.machine_id}>
									{/* Only add data-tour to the first machine card */}
									<MachineCard
										product={machine}
										iconsLoading={Boolean(iconsLoadingById[machine.machine_id])}
										data-tour={index === 0 ? "machine-card" : undefined}
										analyticsContext={{
											customer_id: selectedSiteData?.cus_id || "",
											customer_name:
												selectedSiteData?.parent_customer_name ||
												selectedSiteData?.customer_name ||
												"",
											customer_number: selectedSiteData?.customer_number || "",

											site_id: selectedSiteData?.site_id || "",
											site_name:
												selectedSiteData?.customer_site_name ||
												selectedSiteData?.site_name ||
												selectedSite ||
												"",
											site_number:
												selectedSiteData?.customer_site_number ||
												selectedSite ||
												"",
											customer_site_number:
												selectedSiteData?.customer_site_number ||
												selectedSite ||
												"",

											legal_entity:
												selectedSiteData?.site_legal_entity ||
												selectedSiteData?.legal_entity ||
												"",
										}}
									/>
								</div>
							))
						)}
					</>
				)}
			</div>

			{!loading && !error && !isSearching && totalPages > 1 && (
				<Box sx={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
					<Pagination
						count={totalPages}
						page={currentPage}
						onChange={(e, page) => setCurrentPage(page)}
						sx={{
							"& .Mui-selected": {
								backgroundColor: "var(--primary-main)",
								color: "white",
								background: "var(--primary-main)",
							},
						}}
					/>
				</Box>
			)}
		</div>
	);
};

export default MyMachines;
